import React, { useState } from "react";

/**
 * Decline modal with optional canned reasons.
 *
 * `templates` is [{ label, text }]. Picking one drops its text into the
 * textarea, which stays fully editable — the admin can tweak the wording before
 * it goes out. Nothing is persisted; the list is whatever the caller passes.
 * Omit the prop and the modal behaves exactly as it did before.
 */
const DeclineRemarkModal = ({
  isOpen,
  onClose,
  onDecline,
  title = "Decline Remark",
  templates = [],
}) => {
  const [remark, setRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDecline = async () => {
    setIsSubmitting(true);
    try {
      await onDecline(remark);
      setRemark("");
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setRemark("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">{title}</h2>

        {templates.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Common reasons
            </p>
            <div className="flex flex-col gap-2">
              {templates.map((t) => {
                // Highlight only while the text is untouched — once the admin
                // edits it, it is no longer that template.
                const isActive = remark.trim() === t.text.trim();
                return (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => setRemark(t.text)}
                    disabled={isSubmitting}
                    title={t.text}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                      isActive
                        ? "border-red-400 bg-red-50 text-red-800"
                        : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                    }`}
                  >
                    <span className="font-medium">{t.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Pick one to fill the message below, then edit it as needed.
            </p>
          </div>
        )}

        <textarea
          className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
          rows="5"
          placeholder="Enter decline reason (optional)..."
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          disabled={isSubmitting}
        />
        <p className="text-xs text-gray-400 mt-1">
          This message is emailed to the provider.
        </p>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDecline}
            disabled={isSubmitting}
            className={`px-4 py-2 rounded-lg text-white ${
              isSubmitting
                ? "bg-red-300 cursor-not-allowed"
                : "bg-red-500 hover:bg-red-600"
            }`}
          >
            {isSubmitting ? "Declining..." : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeclineRemarkModal;
